import React, { FC, memo, useEffect, useState, useRef, useCallback } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useGetUserInvoicesQuery } from 'entities/User';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { Invoice, InvoiceCard } from 'entities/Invoice';
import { Button } from 'shared/ui/Button';

export const MobileMyInvoicesPage: FC = memo(() => {
  const { t } = useAppTranslation(['profile', 'booking', 'common']);
  const { userId } = useParams();
  const { isDesktop } = useScreenBreakpoints();
  const navigate = useNavigate();

  const limit = 10;

  const [page, setPage] = useState(1);
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [isLastPage, setIsLastPage] = useState(false);

  const { data, isFetching } = useGetUserInvoicesQuery({ userId, limit, page }, { skip: !userId });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isDesktop && userId) {
      navigate(`${AppRoutes.PROFILE}/${userId}`);
    }
  }, [isDesktop, navigate, userId]);

  useEffect(() => {
    if (data?.content) {
      setAllInvoices((prev) => {
        const newInvoices = data.content.filter((newInvoice) => !prev.some((invoice) => invoice.invoiceId === newInvoice.invoiceId));
        return [...prev, ...newInvoices];
      });
      setIsLastPage(Boolean(data.isLastPage));
    }
  }, [data]);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el || isFetching || isLastPage) {
      return;
    }

    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;

    if (nearBottom) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, isLastPage]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    el.addEventListener('scroll', handleScroll);

    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  if (!userId) {
    return <div>{t('User not exists')}</div>;
  }

  return (
    <div className="px-4">
      <div className="flex flex-col p-4 bg-white rounded-lg gap-2">
        <div ref={containerRef} className="space-y-2.5 max-h-[calc(100vh-theme(spacing.headerHeight)-140px)] pr-2 overflow-y-auto">
          {allInvoices.length ? (
            allInvoices.map((invoice) => <InvoiceCard key={invoice.documentNumber} invoice={invoice} />)
          ) : (
            <div className="text-xl text-primaryLight">{t('No data', { ns: 'common' })}</div>
          )}

          {isFetching && <div className="text-center py-2">{t('Loading...')}</div>}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 w-full z-20 bg-backgroundColor">
        <Link to={`${AppRoutes.PROFILE}/${userId}`} className="text-primary text-lg">
          <div className="flex justify-center py-2 px-4">
            <Button containerClassName="w-full" size="medium">
              {t('Profile')}
            </Button>
          </div>
        </Link>
      </div>
    </div>
  );
});
